/**
 * OperatorShell — persistent nav frame around every operator route
 * (design card 3c/3d). Org switcher on top, a flat primary nav, and
 * collapsible Website/Settings groups; footer carries the account menu
 * and the light/dark toggle. Theme-aware via Radix tokens, so it renders
 * in whichever appearance the global mode resolves to.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { DropdownMenu } from '@radix-ui/themes';
import { OrganizationSwitcher } from '@clerk/react';
import ErrorBoundary from '../shared/ErrorBoundary';
import ThemeToggle from '../shared/ThemeToggle';
import TrialBanner from '../Billing/TrialBanner';
import { usePlatformAdmin } from '../../utils/platform';
import { useAuth, CLERK_ENABLED } from '../Auth/AuthGate';
import { useSiteConfig } from '../../utils/siteConfig';
import api from '../../utils/api';
import { PRODUCT_COMPANY_LINE } from '../../utils/productBrand';
import './OperatorShell.css';

interface NavItem {
  to: string;
  label: string;
  // Optional live badge (e.g. tonight's game-night count).
  badge?: number;
}

// Primary destinations — flat, no group label (design 3c/3d).
const PRIMARY: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/quizzes', label: 'Quizzes' },
  { to: '/live-events', label: 'Game Nights' },
  { to: '/teams', label: 'Teams' },
  { to: '/analytics', label: 'Game Analytics' },
  { to: '/attendance', label: 'Venue Analytics' },
  { to: '/seasons', label: 'Seasons' },
];

// ownerOnly groups/items are hidden from GUEST operators (the backend also
// enforces this — a guest hitting these routes by URL gets a 403).
const GROUPS: { key: string; label: string; ownerOnly?: boolean; items: (NavItem & { ownerOnly?: boolean })[] }[] = [
  {
    key: 'build',
    label: 'Build',
    items: [
      { to: '/slides', label: 'Slides' },
    ],
  },
  {
    key: 'website',
    label: 'Website',
    ownerOnly: true, // guests don't manage the public marketing site
    items: [
      { to: '/home-page', label: 'Home Page' },
      { to: '/schedule', label: 'Public Schedule' },
      { to: '/site-content', label: 'Site Content' },
    ],
  },
  {
    key: 'settings',
    label: 'Settings',
    items: [
      { to: '/venues', label: 'Venues' },
      { to: '/branding', label: 'Branding', ownerOnly: true },
      { to: '/users', label: 'Users', ownerOnly: true },
      { to: '/billing', label: 'Billing', ownerOnly: true },
    ],
  },
];

// Shown only to platform admins (PLATFORM_ADMIN_EMAILS on the backend).
const PLATFORM_GROUP: (typeof GROUPS)[number] = {
  key: 'platform',
  label: 'Platform',
  items: [{ to: '/platform', label: 'Organizations' }],
};

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

const DEFAULT_GROUPS: Record<string, boolean> = { build: true, website: true, settings: false };

function readGroups(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem('shell:groups');
    // Merge over the defaults so a group added later starts in its
    // default state for a host who saved the old set.
    if (raw) return { ...DEFAULT_GROUPS, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return DEFAULT_GROUPS;
}

const OperatorShell: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  // Org-scoped config (see the Operator wrapper in App.tsx). The server
  // always sends brandName, so the org name is only a load-time fallback.
  const { get, meta } = useSiteConfig();
  const brandName = get('brandName', meta?.orgName ?? '');
  const isGuest = user?.role === 'GUEST';
  const isPlatformAdmin = usePlatformAdmin();
  const groups = isPlatformAdmin ? [...GROUPS, PLATFORM_GROUP] : GROUPS;

  const [groupsOpen, setGroupsOpen] = useState<Record<string, boolean>>(readGroups);
  const toggleGroup = (key: string) => {
    setGroupsOpen((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem('shell:groups', JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  // Tonight badge — count of game nights scheduled for today.
  const [tonightCount, setTonightCount] = useState(0);
  useEffect(() => {
    let mounted = true;
    api.get('/api/events').then((res) => {
      if (!mounted) return;
      const events: { date: string }[] = res.data?.data?.events ?? [];
      const now = new Date();
      setTonightCount(events.filter((e) => e.date && isSameDay(new Date(e.date), now)).length);
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  const primary = useMemo(
    () => PRIMARY.map((i) => (i.to === '/live-events' ? { ...i, badge: tonightCount } : i)),
    [tonightCount],
  );

  // Mobile drawer — the sidebar slides in over the page below 800px and
  // closes itself on every navigation.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { setDrawerOpen(false); }, [pathname]);

  const initial = (brandName.trim()[0] || '').toUpperCase();
  const userInitial = (user?.email?.trim()[0] || 'U').toUpperCase();
  const userName = user?.email ? user.email.split('@')[0] : 'Account';

  return (
    <div className="op-shell">
      <header className="op-mobilebar">
        <button
          type="button"
          className="op-menu-btn"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open menu"
          aria-expanded={drawerOpen}
          aria-controls="op-sidebar"
        >
          <span /><span /><span />
        </button>
        <span className="op-org-tile" style={{ background: get('brandColor', '#840032') }}>{initial}</span>
        <span className="op-org-name">{brandName}</span>
      </header>
      {drawerOpen && (
        <div className="op-backdrop" onClick={() => setDrawerOpen(false)} aria-hidden="true" />
      )}

      <aside id="op-sidebar" className={`op-sidebar${drawerOpen ? ' open' : ''}`}>
        {/* Org switcher / brand pill */}
        <div className="op-org">
          {CLERK_ENABLED ? (
            <OrganizationSwitcher hidePersonal />
          ) : (
            <>
              <span className="op-org-tile" style={{ background: get('brandColor', '#840032') }}>{initial}</span>
              <span className="op-org-name">{brandName}</span>
              <span className="op-org-caret" aria-hidden="true">▾</span>
            </>
          )}
        </div>

        <nav className="op-nav">
          {primary.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `op-navitem${isActive ? ' active' : ''}`}
            >
              <span className="op-dot" />
              <span className="op-navlabel">{item.label}</span>
              {item.badge ? (
                <span className="op-badge">{item.badge} tonight</span>
              ) : null}
            </NavLink>
          ))}
        </nav>

        {groups.filter((group) => !(isGuest && group.ownerOnly)).map((group) => {
          const items = group.items.filter((item) => !(isGuest && item.ownerOnly));
          if (items.length === 0) return null;
          const open = groupsOpen[group.key];
          return (
            <div className="op-group" key={group.key}>
              <button
                type="button"
                className="op-group-header"
                onClick={() => toggleGroup(group.key)}
                aria-expanded={open}
              >
                {group.label.toUpperCase()}
                <span aria-hidden="true">{open ? '▾' : '▸'}</span>
              </button>
              {open && (
                <div className="op-group-items">
                  {items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) => `op-subitem${isActive ? ' active' : ''}`}
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        <div className="op-footer">
          <ThemeToggle compact />
          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <button type="button" className="op-account">
                <span className="op-avatar">{userInitial}</span>
                <span className="op-account-id">
                  <span className="op-account-name">{userName}</span>
                  {user?.email && <span className="op-account-email" title={user.email}>{user.email}</span>}
                </span>
                <span className="op-account-caret" aria-hidden="true">↗</span>
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
              {user?.email && (
                <>
                  <DropdownMenu.Label>{user.email}</DropdownMenu.Label>
                  <DropdownMenu.Separator />
                </>
              )}
              <DropdownMenu.Item color="red" onSelect={logout}>Log out</DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
          <span className="op-company">{PRODUCT_COMPANY_LINE}</span>
        </div>
      </aside>

      <main className="op-main">
        <TrialBanner />
        <ErrorBoundary>{children}</ErrorBoundary>
      </main>
    </div>
  );
};

export default OperatorShell;
